import React from 'react';
import { Box, Button, Chip, Stack, Typography } from '@mui/joy';
import { Link } from 'react-router-dom';
import {
  formatWeekDayLabel,
  type ScheduledJobRow,
  type WorkspaceOperations,
} from './workspaceDashboard.model';
import { formatCompletionStatus } from './openJobCards.model';

const TABLE_SX = {
  width: '100%',
  borderCollapse: 'collapse',
  '& th': { textAlign: 'left', fontSize: '0.75rem', color: 'neutral.500', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' },
  '& td': { py: 1, borderBottom: '1px solid', borderColor: 'divider' },
} as const;

function CompletionStatus({ row }: { row: ScheduledJobRow }) {
  if (!row.jobCardId) {
    return <Typography level="body-sm" sx={{ color: 'neutral.500' }}>No job card</Typography>;
  }
  return (
    <Chip
      size="sm"
      variant="soft"
      color={row.completion?.kind === 'completed' ? 'success' : 'neutral'}
      sx={{ whiteSpace: 'normal', '--Chip-minHeight': 'auto', py: 0.25 }}
    >
      {formatCompletionStatus(row.completion)}
    </Chip>
  );
}

export const WorkspaceDashboardScheduled: React.FC<{ ops: WorkspaceOperations }> = ({ ops }) => {
  return (
    <Stack gap={1.5}>
      <Box>
        <Typography level="title-lg">Scheduled</Typography>
        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
          Diarised install or delivery dates. Current and upcoming first, then older dates.
          Marked-complete workspaces leave this list.
        </Typography>
      </Box>
      {ops.scheduled.length === 0 ? (
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
          No diarised jobs on the active book.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={TABLE_SX}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Workspace</th>
                <th>Customer</th>
                <th>Crew</th>
                <th>Source</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {ops.scheduled.map((row) => (
                <tr key={`${row.projectId}-${row.jobCardId || row.kind}`}>
                  <td>
                    <Typography level="body-sm">
                      {row.kind === 'delivery' ? 'Delivery' : 'Install'} · {formatWeekDayLabel(row.dayKey)}
                    </Typography>
                    {row.spanNote && (
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>{row.spanNote}</Typography>
                    )}
                  </td>
                  <td><Typography level="body-sm">{row.time}</Typography></td>
                  <td>
                    <Typography
                      level="body-sm"
                      fontWeight="lg"
                      component={Link}
                      to={row.href}
                      sx={{ textDecoration: 'none' }}
                    >
                      {row.workspace}
                    </Typography>
                  </td>
                  <td><Typography level="body-sm">{row.customer}</Typography></td>
                  <td><Typography level="body-sm">{row.crew}</Typography></td>
                  <td><Typography level="body-sm">{row.source}</Typography></td>
                  <td><CompletionStatus row={row} /></td>
                  <td>
                    <Button size="sm" variant="plain" component={Link} to={row.href}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export const WorkspaceDashboardUnscheduled: React.FC<{ ops: WorkspaceOperations }> = ({ ops }) => {
  return (
    <Stack gap={1.5}>
      <Box>
        <Typography level="title-lg">Not Scheduled</Typography>
        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
          A production date is set, but there is no install or delivery date yet.
          Current and upcoming production dates first, then older.
        </Typography>
      </Box>
      {ops.unscheduled.length === 0 ? (
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
          Nothing waiting on an install or delivery date.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box component="table" sx={TABLE_SX}>
            <thead>
              <tr>
                <th>Production date</th>
                <th>Workspace</th>
                <th>Customer</th>
                <th>Waiting for</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {ops.unscheduled.map((row) => (
                <tr key={row.projectId}>
                  <td><Typography level="body-sm">{formatWeekDayLabel(row.productionDate)}</Typography></td>
                  <td>
                    <Typography
                      level="body-sm"
                      fontWeight="lg"
                      component={Link}
                      to={row.href}
                      sx={{ textDecoration: 'none' }}
                    >
                      {row.workspace}
                    </Typography>
                  </td>
                  <td><Typography level="body-sm">{row.customer}</Typography></td>
                  <td>
                    <Typography level="body-sm">
                      {row.kind === 'delivery' ? 'Delivery date' : 'Install date'}
                    </Typography>
                  </td>
                  <td>
                    <Button size="sm" variant="plain" component={Link} to={row.href}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Box>
        </Box>
      )}
    </Stack>
  );
};
